/**
 * Structured error when the backend rejects due to subscription/usage limit (402/403).
 * Callers can detect this and show UpgradeLimitModal with the given limitType.
 */
export type SubscriptionLimitType = 'edits' | 'posts' | 'images' | 'topics' | 'generic'

export class SubscriptionLimitError extends Error {
  readonly limitType: SubscriptionLimitType
  readonly status: number

  constructor(limitType: SubscriptionLimitType = 'generic', status: number = 402) {
    super(`Subscription limit reached: ${limitType}`)
    this.name = 'SubscriptionLimitError'
    this.limitType = limitType
    this.status = status
    Object.setPrototypeOf(this, SubscriptionLimitError.prototype)
  }
}

export function isSubscriptionLimitError(err: unknown): err is SubscriptionLimitError {
  return err instanceof SubscriptionLimitError
}

/**
 * Message shape for limit detection (minimal to avoid coupling to chatService types)
 */
export interface MessageLike {
  sender_type?: string
  message?: { type?: string; content?: string | Record<string, unknown> }
}

/**
 * Detect if the backend returned a "limit reached" system message (200 with limit text).
 * Backend returns e.g. sender_type: "system", content: "You have reached your monthly limit for posts (2/2)..."
 */
export function isLimitReachedResponse(messages: MessageLike[] | null | undefined): boolean {
  if (!messages || messages.length !== 1) return false
  const msg = messages[0]
  if (msg.sender_type !== 'system') return false
  const content = msg.message?.content
  const text = typeof content === 'string' ? content : ''
  return (
    text.includes('reached your monthly limit') ||
    text.includes('upgrade to Pro for more') ||
    text.includes('monthly limit for')
  )
}

/**
 * Infer limit type from backend limit message content.
 */
export function getLimitTypeFromLimitMessage(messages: MessageLike[] | null | undefined): SubscriptionLimitType {
  if (!messages || messages.length !== 1) return 'generic'
  const content = messages[0].message?.content
  const text = (typeof content === 'string' ? content : '').toLowerCase()
  if (text.includes('limit for posts') || text.includes('limit for post')) return 'posts'
  if (text.includes('limit for edits') || text.includes('limit for edit')) return 'edits'
  if (text.includes('limit for images') || text.includes('limit for image')) return 'images'
  if (text.includes('limit for topics') || text.includes('limit for topic')) return 'topics'
  return 'generic'
}
